import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, SyntheticBanner } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, MessageSquarePlus } from "lucide-react";

export default function PilotConfiguration() {
  const { roleCode, persona } = useApp();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ answer: "", source: "", notes: "", status: "Partially Answered" });

  const load = useCallback(() => api.pilotConfig().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-500">Loading pilot configuration…</div>;

  const capture = (f) => { setTarget(f); setForm({ answer: "", source: "", notes: "", status: "Partially Answered" }); setOpen(true); };
  const submit = async () => {
    await api.capturePilotAnswer({ field_key: target.key, ...form, role_code: roleCode, captured_by: persona?.name });
    toast.success(`Captured validation answer for “${target.label}”`);
    setOpen(false); load();
  };

  return (
    <div>
      <PageHeader testid="pilot-config-header" title="Pilot Configuration"
        subtitle="Confirmed facts and unresolved assumptions for the next Keenan/Kevin validation session. Capture answers live to update requirements." />
      <SyntheticBanner />

      <div className="grid gap-3 md:grid-cols-2">
        {data.fields.map((f) => {
          const confirmed = f.status === "CONFIRMED";
          return (
            <Card key={f.key} className="p-4" data-testid={`pilot-field-${f.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {confirmed ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <HelpCircle className="h-4 w-4 text-amber-600" />}
                    <span className="text-sm font-semibold text-slate-800">{f.label}</span>
                    <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-500">{f.category}</span>
                  </div>
                  <div className={`mt-1 text-sm ${confirmed ? "font-semibold text-slate-900" : "font-medium text-amber-700"}`}>
                    {confirmed ? f.value : "To Be Confirmed During Domain Validation"}
                  </div>
                  {f.captured && (
                    <div className="mt-2 rounded-md bg-teal-50 p-2 text-xs text-teal-900">
                      <div><strong>Answer:</strong> {f.captured.answer || "—"}</div>
                      <div className="text-[10px] text-teal-700">{f.captured.source} · {f.captured.date} · {f.captured.status}</div>
                    </div>
                  )}
                </div>
                {!confirmed && (
                  <Button size="sm" variant="outline" data-testid={`capture-${f.key}`} onClick={() => capture(f)}>
                    <MessageSquarePlus className="mr-1 h-3.5 w-3.5" /> Capture
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capture Validation Answer</DialogTitle>
            <DialogDescription>{target?.label}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Answer</Label><Input data-testid="pc-answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} /></div>
            <div><Label className="text-xs">Source / person</Label><Input data-testid="pc-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. Keenan (lab ops)" /></div>
            <div><Label className="text-xs">Notes</Label><Textarea data-testid="pc-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div><Label className="text-xs">Confidence / status</Label>
              <div className="mt-1 flex gap-1">
                {["Partially Answered", "Confirmed", "Requires External Vendor", "Requires Compliance Review"].map((s) => (
                  <button key={s} data-testid={`pc-status-${s.split(" ")[0].toLowerCase()}`} onClick={() => setForm({ ...form, status: s })}
                    className={`rounded border px-2 py-1 text-[10px] ${form.status === s ? "border-teal-500 bg-teal-50 text-teal-700" : "border-slate-300 text-slate-500"}`}>{s}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="pc-submit" className="bg-teal-600 hover:bg-teal-700" onClick={submit}>Save Answer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
